import { CategoryNode } from '../types/category';
import { FIELD_LABELS } from './fieldLabels';

export type CategoryTreeRow = {
  l1: string;
  l2: string;
  l3: string;
};

const CHANGE_REQUEST_LABEL = 'בקשת שינוי';

export const flattenCategoryTree = (nodes: CategoryNode[]): CategoryTreeRow[] => {
  const rows: CategoryTreeRow[] = [];
  nodes.forEach((level1) => {
    const level2Nodes = level1.children || [];
    if (level2Nodes.length === 0) {
      rows.push({ l1: level1.name, l2: '', l3: '' });
      return;
    }
    level2Nodes.forEach((level2) => {
      const level3Nodes = level2.children || [];
      if (level3Nodes.length === 0) {
        rows.push({ l1: level1.name, l2: level2.name, l3: '' });
        return;
      }
      level3Nodes.forEach((level3) => {
        rows.push({ l1: level1.name, l2: level2.name, l3: level3.name });
      });
    });
  });
  return rows;
};

const escapeCsv = (value: string) => `"${value.replace(/"/g, '""')}"`;

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

export const downloadCategoryTreeExcel = (nodes: CategoryNode[], fileDate: string) => {
  const rows = flattenCategoryTree(nodes);
  const headers = [
    FIELD_LABELS.category1Short,
    FIELD_LABELS.category2Short,
    FIELD_LABELS.category3Short,
    CHANGE_REQUEST_LABEL,
  ];
  const csvRows = [
    headers.map(escapeCsv).join(','),
    ...rows.map((row) => [row.l1, row.l2, row.l3, ''].map(escapeCsv).join(',')),
  ];
  const blob = new Blob([`\uFEFF${csvRows.join('\n')}`], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `metrica-category-tree_${fileDate}.csv`;
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const buildPrintHtml = (nodes: CategoryNode[], tenantName: string, printedOn: string) => {
  const groups = nodes.map((level1) => {
    const level2Nodes = level1.children || [];
    const children = level2Nodes
      .map((level2) => {
        const level3Nodes = level2.children || [];
        const leaves = level3Nodes
          .map((level3) => `<div class="l3">${escapeHtml(level3.name)}</div>`)
          .join('');
        return `<div class="l2-block"><div class="l2">${escapeHtml(level2.name)}</div>${leaves}</div>`;
      })
      .join('');
    return `<section class="group"><div class="l1">${escapeHtml(level1.name)}</div>${children}</section>`;
  }).join('');

  const tenantLine = tenantName ? `<div>${escapeHtml(tenantName)}</div>` : '';

  return `<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="utf-8" />
  <title>מבנה קטגוריות</title>
  <style>
    @page { size: A4; margin: 14mm; }
    body {
      margin: 0;
      font-family: Heebo, Arial, sans-serif;
      direction: rtl;
      color: #1a2e24;
    }
    h1 { margin: 0 0 6px; font-size: 22px; }
    .meta { margin: 0 0 18px; font-size: 12px; color: #5c6b63; line-height: 1.5; }
    .legend { display: flex; gap: 16px; margin-bottom: 16px; font-size: 13px; font-weight: 700; }
    .legend .l1 { margin: 0; }
    .legend .l2 { margin: 0; }
    .legend .l3 { margin: 0; }
    .group { page-break-inside: avoid; margin-bottom: 10px; }
    .l1 { color: #1B9E77; font-size: 16px; font-weight: 700; margin: 8px 0 4px; }
    .l2 { color: #D95F02; font-size: 14px; font-weight: 700; margin: 3px 72px 2px 0; }
    .l3 { color: #7570B3; font-size: 13px; font-weight: 600; margin: 1px 144px 1px 0; }
  </style>
</head>
<body>
  <h1>מבנה קטגוריות</h1>
  <div class="meta">
    ${tenantLine}
    <div>${escapeHtml(printedOn)}</div>
  </div>
  <div class="legend">
    <span class="l1">${escapeHtml(FIELD_LABELS.category1Short)}</span>
    <span class="l2">${escapeHtml(FIELD_LABELS.category2Short)}</span>
    <span class="l3">${escapeHtml(FIELD_LABELS.category3Short)}</span>
  </div>
  ${groups || '<div>אין קטגוריות</div>'}
</body>
</html>`;
};

export const printCategoryTreePdf = (
  nodes: CategoryNode[],
  tenantName: string,
  printedOn: string
) => {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  printWindow.document.open();
  printWindow.document.write(buildPrintHtml(nodes, tenantName, printedOn));
  printWindow.document.close();
  printWindow.onafterprint = () => printWindow.close();
  const triggerPrint = () => printWindow.print();
  if (printWindow.document.readyState === 'complete') {
    setTimeout(triggerPrint, 50);
  } else {
    printWindow.onload = triggerPrint;
  }
};
